import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, MutableRefObject } from 'react';
import type { ShellHydration } from '../../../api.js';
import type { Palette } from '../../../_theme/palette.js';
import { LinearProgress } from '../../../_components/ProgressBar.js';
import { type Direction, questionAnim } from '../../../motion/keyframes.js';
import { AUTO_ADVANCE_TYPES, useAutoAdvance } from '../hooks/useAutoAdvance.js';
import { usePageKeyboard } from '../hooks/usePageKeyboard.js';
import type { PublicSurvey, SurveyQuestion } from '../types.js';
import { valueForLetterKey, valueForNumberKey } from '../utils/keyboardMap.js';
import { validateAnswerDetailed } from '../utils/validation.js';
import { BrandingMark } from './BrandingMark.js';
import { ConversationalQuestion } from './ConversationalQuestion.js';
import { ConversationalChevrons } from './ConversationalChevrons.js';

interface ConversationalSurveyProps {
  palette: Palette;
  survey: PublicSurvey;
  questions: SurveyQuestion[];
  questionIndex: number;
  setQuestionIndex: (index: number) => void;
  // Shared with the parent so paged↔conversational mode flips don't reset
  // the directional context. Parent flips this to 'backward' on retreat.
  directionRef: MutableRefObject<Direction>;
  responses: Record<string, unknown>;
  setResponses: (updater: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  hydration: ShellHydration;
  submitting: boolean;
  error: string | null;
  setError: (message: string | null) => void;
  onSubmit: () => Promise<void>;
  // Called when validation rejects user input — drives the validation_failed
  // analytics event so dashboards can surface fields that customers struggle with.
  onValidationFailed?: (questionKey: string, rule: string, message: string) => void;
}

export function ConversationalSurvey({
  palette,
  survey,
  questions,
  questionIndex,
  setQuestionIndex,
  directionRef,
  responses,
  setResponses,
  hydration,
  submitting,
  error,
  setError,
  onSubmit,
  onValidationFailed,
}: ConversationalSurveyProps): JSX.Element {
  const total = questions.length;
  const current = questions[questionIndex];
  const isLast = questionIndex >= total - 1;
  const showProgress = survey.appearance?.showProgressBar !== false && total > 1;
  const progressRatio = total > 0 ? (questionIndex + 1) / total : 0;

  const advance = useMemo(
    () => async (): Promise<void> => {
      if (!current) return;
      const failure = validateAnswerDetailed(current, responses[current.key]);
      if (failure) {
        setError(failure.message);
        onValidationFailed?.(current.key, failure.rule, failure.message);
        return;
      }
      setError(null);
      if (isLast) {
        await onSubmit();
        return;
      }
      directionRef.current = 'forward';
      setQuestionIndex(questionIndex + 1);
    },
    [current, responses, isLast, onSubmit, questionIndex, setError, setQuestionIndex, directionRef, onValidationFailed],
  );

  const retreat = (): void => {
    setError(null);
    if (questionIndex > 0) {
      directionRef.current = 'backward';
      setQuestionIndex(questionIndex - 1);
    }
  };

  const setCurrentValue = (next: unknown): void => {
    if (!current) return;
    setResponses((prev) => ({ ...prev, [current.key]: next }));
  };

  // Auto-advance default is on for conversational mode (single question on
  // screen so it's never ambiguous which question to advance).
  const autoAdvanceEnabled = survey.appearance?.autoAdvance !== false;
  useAutoAdvance({
    question: current,
    value: current ? responses[current.key] : undefined,
    enabled: autoAdvanceEnabled,
    onAdvance: () => void advance(),
  });

  // Pending-advance hint: shown as "Continuing →" beside the OK button so
  // the respondent sees a deliberate hand-off rather than a sudden slide.
  // Tracks eligibility AND "answer changed on this question" so that
  // navigating Back to an already-answered question doesn't show a phantom
  // countdown — useAutoAdvance primes its baseline on first visit and won't
  // fire from re-entry.
  const baselineRef = useRef<{ key: string | null; value: unknown }>({ key: null, value: undefined });
  const [advancePending, setAdvancePending] = useState(false);
  useEffect(() => {
    if (!autoAdvanceEnabled || !current) {
      setAdvancePending(false);
      return;
    }
    const eligible = AUTO_ADVANCE_TYPES.has(current.type);
    const value = responses[current.key];
    const meaningful = !(
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.length === 0) ||
      (Array.isArray(value) && value.length === 0)
    );
    if (!eligible || !meaningful) {
      baselineRef.current = { key: current.key, value };
      setAdvancePending(false);
      return;
    }
    if (baselineRef.current.key !== current.key) {
      // First time we land on this question — even if the value is already
      // populated (Back navigation, partial resume), don't promise an
      // auto-advance that won't fire.
      baselineRef.current = { key: current.key, value };
      setAdvancePending(false);
      return;
    }
    if (baselineRef.current.value === value) {
      // Same value as we arrived with — no countdown.
      setAdvancePending(false);
      return;
    }
    setAdvancePending(true);
  }, [autoAdvanceEnabled, current, responses]);

  usePageKeyboard({
    onEnter: () => void advance(),
    onNumberKey: (key) => {
      const v = valueForNumberKey(current, key);
      if (v !== undefined) setCurrentValue(v);
    },
    onLetterKey: (index) => {
      const v = valueForLetterKey(current, index);
      if (v !== undefined) setCurrentValue(v);
    },
  });

  return (
    <div style={containerStyle(palette)}>
      {showProgress && (
        <div style={progressRowStyle()}>
          <LinearProgress palette={palette} ratio={progressRatio} />
        </div>
      )}
      <div style={counterStyle(palette)}>
        {questionIndex + 1} of {total}
      </div>
      <div style={bodyStyle()}>
        {current && (
          // Keying on questionIndex re-mounts the question on each advance
          // so chirp-question-in replays. Direction comes from the parent
          // ref so retreat slides in from the left, advance from the right.
          <div
            key={current.key + '-' + questionIndex}
            style={{ animation: questionAnim(directionRef.current) }}
          >
            <ConversationalQuestion
              palette={palette}
              question={current}
              questionNumber={questionIndex + 1}
              value={responses[current.key]}
              hydration={hydration}
              onChange={(next) => setResponses((prev) => ({ ...prev, [current.key]: next }))}
              onConfirm={advance}
              submitting={submitting}
              isLast={isLast}
              advancePending={advancePending}
              error={error}
            />
          </div>
        )}
        <BrandingMark
          palette={palette}
          theme={survey.theme}
          hideBranding={survey.appearance?.hideBranding}
          topGap={20}
        />
      </div>
      <ConversationalChevrons
        palette={palette}
        canGoBack={questionIndex > 0}
        canGoForward={questionIndex < total - 1}
        onUp={retreat}
        onDown={() => void advance()}
      />
    </div>
  );
}

function containerStyle(palette: Palette): CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    background: palette.background,
    color: palette.text,
  };
}

function progressRowStyle(): CSSProperties {
  return { padding: '0 0 8px' };
}

function counterStyle(palette: Palette): CSSProperties {
  return {
    // Right padding kept clear of the absolutely-positioned close button
    // (32px wide at right:12, so total reserved area = 44px + buffer).
    padding: '12px 60px 0 32px',
    textAlign: 'right',
    fontSize: 13,
    color: palette.textMuted,
    fontWeight: 600,
  };
}

function bodyStyle(): CSSProperties {
  return {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: '32px 32px 120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };
}
